// FunctionsReactQuillCustomized.jsx
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CSSReactQuillCustomizedStyles.css';

// Import Parchment to use for custom attributors
const Parchment = Quill.import('parchment');

// Define size whitelist using pixel equivalents of point sizes
const Size = Quill.import('formats/size');
Size.whitelist = [
    '8px', '9px', '10px', '11px', '12px', '14px', '15px',
    '16px', // default value
    '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px', '72px', '96px', '100px'
];
Quill.register(Size, true);

// ✅ Define and register custom line-height attributor
const LineHeightStyle = new Parchment.Attributor.Style('line-height', 'line-height', {
    scope: Parchment.Scope.BLOCK,
    whitelist: ['1', '1.5', '2', '2.5', '3'],
});
Quill.register(LineHeightStyle, true);

const FunctionsReactQuillCustomized = forwardRef(({ content, onChange, onFocus }, ref) => {

    useImperativeHandle(ref, () => ({
        getEditor: () => quillRef.current?.getEditor()
    }));

    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (editor) {
            editor.format('size', '16px');
            editor.format('line-height', '1.5');
        }
    }, []);

    const quillRef = React.useRef(null);

    const modules = {
        toolbar: [
            [{ 'font': [] }],
            [{ 'size': Size.whitelist }],
            [{ 'line-height': ['1', '1.5', '2', '2.5', '3'] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
        ],
    };
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const baseStyle = {
        minHeight: '100px',            // or your preferred minimum height
        // border: '1px solid green',
        boxSizing: 'border-box',
        transition: 'box-shadow 0.5s ease, border-color 0.3s ease',
        //height: 'auto',               // allow auto-expansion
        height: 'calc(100% - 14px)',              // subtract 5px from full height
        overflow: 'visible',          // ensure content isn't clipped
    };

    const hoverStyle = isHovered ? {
        borderColor: 'black',
        boxShadow: 'inset 0 0 10px 2px #97C1FF',
    } : {};

    const focusStyle = isFocused ? {
        borderColor: 'black',
        boxShadow: 'inset 0 0 10px 2px #8ACF98',
    } : {};

    const combinedStyle = {
        ...baseStyle,
        ...hoverStyle,
        ...focusStyle,
    };

    return (
        <div
            className="functions-react-quill-customized-wrapper"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={combinedStyle}
        >
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={onChange}
                modules={modules}
                onFocus={(e) => {
                    setIsFocused(true);
                    if (onFocus) onFocus(e);
                }}
                onBlur={() => setIsFocused(false)}
            />
        </div>
    );
});

export default FunctionsReactQuillCustomized;